import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { theme, card, emptyFrame, btnPrimary, btnSecondary, inputStyle, type as text } from "../theme";
import { DimensionHeading, SheetCard } from "./Drawing";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate, fmtDateTime } from "../lib/format";

// Platform Admin → Control mappings (docs/CONTROL-MAPPINGS.md). The owner's work on the
// platform's reference mappings between standards: derive the Harmonized Structure pairs, import a
// published crosswalk under its own name and licence, ask the AI to draft mappings between two
// standards, and approve or reject what is proposed. Members only ever see approved mappings.

type MappingStatus = "proposed" | "approved" | "rejected";
type MappingSource = "harmonised_structure" | "published" | "ai_draft";
type MappingStrength = "equivalent" | "partial" | "related";

interface MappingControl { id: string; frameworkId: string; name: string }

interface MappingRow {
  id: string;
  strength: MappingStrength;
  source: MappingSource;
  sourceRef: string | null;
  licence: string | null;
  rationale: string | null;
  status: MappingStatus;
  decidedAt: string | null;
  decisionNote: string | null;
  draftRunId: string | null;
  controlA: MappingControl;
  controlB: MappingControl;
}

interface DraftRun {
  id: string;
  fromFrameworkId: string;
  toFrameworkId: string;
  status: string;
  proposed: number | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

interface AdminData {
  rows: MappingRow[];
  counts: { byStatus: Record<string, number>; bySource: Record<string, number> };
  runs: DraftRun[];
  frameworks: Array<{ id: string; label: string; controls: number }>;
}

interface ImportResult { created: number; updated: number; unchanged: number; errors: string[] }

const STATUS_LABEL: Record<MappingStatus, string> = { proposed: "Proposed", approved: "Approved", rejected: "Rejected" };
const SOURCE_LABEL: Record<MappingSource, string> = { harmonised_structure: "Harmonized Structure", published: "Published crosswalk", ai_draft: "AI draft" };
const STRENGTH_LABEL: Record<MappingStrength, string> = { equivalent: "Equivalent", partial: "Partial", related: "Related" };
const RUN_STATUS_LABEL: Record<string, string> = { queued: "Queued", running: "Running", done: "Done", error: "Failed" };

const small: React.CSSProperties = { ...btnSecondary, padding: "6px 12px", fontSize: 12 };
const smallPrimary: React.CSSProperties = { ...btnPrimary, padding: "6px 12px", fontSize: 12 };
const select: React.CSSProperties = { ...inputStyle, width: "auto", minWidth: 160, padding: "6px 10px" };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 12, fontWeight: 600, color: theme.color.muted, borderBottom: `1px solid ${theme.color.line}`, whiteSpace: "nowrap" };
const td: React.CSSProperties = { padding: "8px 10px", fontSize: 12, color: theme.color.text, borderBottom: `1px solid ${theme.color.border}`, verticalAlign: "top" };

function statusTone(s: MappingStatus): { color: string; border: string } {
  if (s === "approved") return { color: theme.color.successText, border: theme.color.success };
  if (s === "rejected") return { color: theme.color.dangerText, border: theme.color.danger };
  return { color: theme.color.warningText, border: theme.color.warning };
}

function Tag({ children, color, border }: { children: React.ReactNode; color: string; border: string }) {
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 2, border: `1px solid ${border}`, color, whiteSpace: "nowrap" }}>{children}</span>;
}

export function ControlMappingsAdminTab() {
  const [data, setData] = useState<AdminData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Filters (server-side) and the selection for Approve / Reject.
  const [status, setStatus] = useState<"" | MappingStatus>("proposed");
  const [source, setSource] = useState<"" | MappingSource>("");
  const [framework, setFramework] = useState("");
  const [draftRunId, setDraftRunId] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [decisionNote, setDecisionNote] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await api.get<AdminData>("/admin/control-mappings", {
        status: status || undefined, source: source || undefined, framework: framework || undefined, draftRunId: draftRunId || undefined,
      });
      setData(d);
      setError(null);
      // Keep only the selected rows still listed.
      setSelected((prev) => new Set([...prev].filter((id) => d.rows.some((r) => r.id === id))));
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the control mappings"));
    }
  }, [status, source, framework, draftRunId]);

  useEffect(() => { void load(); }, [load]);

  // Poll every 5 s while a drafting pass is queued or running.
  const active = data?.runs.some((r) => r.status === "queued" || r.status === "running") ?? false;
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => { void loadRef.current(); }, 5000);
    return () => window.clearInterval(t);
  }, [active]);

  const fwLabel = useMemo(() => {
    const m = new Map((data?.frameworks ?? []).map((f) => [f.id, f.label]));
    return (id: string) => m.get(id) ?? id;
  }, [data]);

  async function run<T>(key: string, fn: () => Promise<T>, done: (r: T) => string) {
    setBusy(key); setError(null); setNotice(null);
    try {
      const r = await fn();
      setNotice(done(r));
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "That did not work"));
    } finally {
      setBusy(null);
    }
  }

  function decide(next: "approved" | "rejected") {
    const ids = [...selected];
    if (!ids.length) return;
    void run("decide", () => api.post<{ decided: number }>("/admin/control-mappings/decide", { ids, status: next, note: decisionNote.trim() || undefined }), (r) => {
      setSelected(new Set());
      setDecisionNote("");
      return `${r.decided} mapping${r.decided === 1 ? "" : "s"} ${next}.`;
    });
  }

  if (!data && !error) return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>;

  const rows = data?.rows ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const byStatus = data?.counts.byStatus ?? {};
  const bySource = data?.counts.bySource ?? {};

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div>
        <div style={{ ...text.sectionTitle }}>Control mappings between standards</div>
        <div style={{ ...text.hint, maxWidth: 820 }}>
          Platform reference data. Members see only approved mappings: on each control ("Also maps to") and on the
          Standards overlap page. A mapping never marks a control as met; it says where evidence accepted for one
          control is worth showing for another.
        </div>
      </div>

      {error && <div style={{ ...card, padding: 12, ...text.body, color: theme.color.dangerText }}>{error}</div>}
      {notice && <div style={{ ...card, padding: 12, ...text.body, color: theme.color.successText }}>{notice}</div>}

      {/* Counts */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {(["proposed", "approved", "rejected"] as MappingStatus[]).map((s) => (
          <button key={s} type="button" onClick={() => setStatus(s)} style={{ ...card, padding: "10px 16px", cursor: "pointer", textAlign: "left", minWidth: 130, borderColor: status === s ? theme.color.mark : theme.color.line }}>
            <div style={text.stat}>{byStatus[s] ?? 0}</div>
            <div style={text.statLabel}>{STATUS_LABEL[s]}</div>
          </button>
        ))}
        {(["harmonised_structure", "published", "ai_draft"] as MappingSource[]).map((s) => (
          <button key={s} type="button" onClick={() => setSource(s)} style={{ ...card, padding: "10px 16px", cursor: "pointer", textAlign: "left", minWidth: 150, borderColor: source === s ? theme.color.mark : theme.color.line }}>
            <div style={text.stat}>{bySource[s] ?? 0}</div>
            <div style={text.statLabel}>{SOURCE_LABEL[s]}</div>
          </button>
        ))}
      </div>

      {/* Sources of mappings */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
        <SheetCard label="Harmonized Structure">
          <div style={{ ...text.hint, marginBottom: 12 }}>
            The ISO management system standards share clauses 4 to 10. Derive the equivalent pairs between every
            ISO standard on the platform again (the seed also does this).
          </div>
          <button type="button" disabled={busy !== null} style={{ ...smallPrimary, opacity: busy ? 0.6 : 1 }}
            onClick={() => run("harmonised", () => api.post<{ written: number }>("/admin/control-mappings/harmonised"), (r) => `${r.written} Harmonized Structure pair${r.written === 1 ? "" : "s"} derived.`)}>
            {busy === "harmonised" ? "Deriving…" : "Derive Harmonized Structure mappings"}
          </button>
        </SheetCard>

        <ImportCrosswalk busy={busy} onRun={run} />

        <DraftWithAi
          busy={busy}
          frameworks={data?.frameworks ?? []}
          runs={data?.runs ?? []}
          fwLabel={fwLabel}
          onRun={run}
          onShowRun={(id) => { setDraftRunId(id); setStatus(""); setSource(""); }}
        />
      </div>

      {/* Filters + table */}
      <div>
        <DimensionHeading count={rows.length >= 500 ? "first 500" : rows.length}>Mappings</DimensionHeading>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <select value={status} onChange={(e) => setStatus(e.target.value as "" | MappingStatus)} style={select} aria-label="Status">
            <option value="">Every status</option>
            {(Object.keys(STATUS_LABEL) as MappingStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <select value={source} onChange={(e) => setSource(e.target.value as "" | MappingSource)} style={select} aria-label="Source">
            <option value="">Every source</option>
            {(Object.keys(SOURCE_LABEL) as MappingSource[]).map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
          </select>
          <select value={framework} onChange={(e) => setFramework(e.target.value)} style={select} aria-label="Standard">
            <option value="">Every standard</option>
            {(data?.frameworks ?? []).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          {draftRunId && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, ...text.hint }}>
              Draft run only
              <button type="button" style={small} onClick={() => setDraftRunId("")}>Show all</button>
            </span>
          )}
        </div>

        {selected.size > 0 && (
          <div style={{ ...card, padding: "10px 14px", marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ ...text.body, fontWeight: 600 }}>{selected.size} selected</span>
            <input value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} maxLength={500} placeholder="Note (optional)" style={{ ...inputStyle, width: 260, padding: "6px 10px" }} />
            <button type="button" disabled={busy !== null} style={{ ...smallPrimary, opacity: busy ? 0.6 : 1 }} onClick={() => decide("approved")}>Approve</button>
            <button type="button" disabled={busy !== null} style={{ ...small, color: theme.color.dangerText, borderColor: theme.color.danger, opacity: busy ? 0.6 : 1 }} onClick={() => decide("rejected")}>Reject</button>
            <button type="button" style={small} onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        {rows.length === 0 ? (
          <div style={{ ...emptyFrame, ...text.hint }}>No mappings match these filters.</div>
        ) : (
          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 32 }}>
                    <input type="checkbox" aria-label="Select all" checked={allSelected}
                      onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} />
                  </th>
                  <th style={th}>Control</th>
                  <th style={th}>Maps to</th>
                  <th style={th}>Strength</th>
                  <th style={th}>Source</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const tone = statusTone(r.status);
                  return (
                    <tr key={r.id}>
                      <td style={td}>
                        <input type="checkbox" aria-label="Select mapping" checked={selected.has(r.id)}
                          onChange={(e) => setSelected((prev) => { const n = new Set(prev); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })} />
                      </td>
                      <td style={td}>
                        <div style={text.meta}>{fwLabel(r.controlA.frameworkId)}</div>
                        <div><span style={{ ...text.ref, color: theme.color.ref, marginRight: 6 }}>{r.controlA.id}</span>{r.controlA.name}</div>
                      </td>
                      <td style={td}>
                        <div style={text.meta}>{fwLabel(r.controlB.frameworkId)}</div>
                        <div><span style={{ ...text.ref, color: theme.color.ref, marginRight: 6 }}>{r.controlB.id}</span>{r.controlB.name}</div>
                        {r.rationale && <div style={{ ...text.meta, marginTop: 4 }}>{r.rationale}</div>}
                      </td>
                      <td style={td}>{STRENGTH_LABEL[r.strength] ?? r.strength}</td>
                      <td style={td}>
                        <div>{SOURCE_LABEL[r.source] ?? r.source}</div>
                        {r.sourceRef && <div style={text.meta}>{r.sourceRef}</div>}
                        {r.licence && <div style={text.meta}>Licence: {r.licence}</div>}
                      </td>
                      <td style={td}>
                        <Tag color={tone.color} border={tone.border}>{STATUS_LABEL[r.status] ?? r.status}</Tag>
                        {r.decidedAt && <div style={{ ...text.meta, marginTop: 4 }}>{fmtDate(r.decidedAt)}</div>}
                        {r.decisionNote && <div style={{ ...text.meta, marginTop: 2 }}>{r.decisionNote}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

type RunFn = <T>(key: string, fn: () => Promise<T>, done: (r: T) => string) => Promise<void>;

// ── Import a published crosswalk ─────────────────────────────────────────────

function ImportCrosswalk({ busy, onRun }: { busy: string | null; onRun: RunFn }) {
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [sourceRef, setSourceRef] = useState("");
  const [licence, setLicence] = useState("");
  const [approve, setApprove] = useState(false);
  const [defaultStrength, setDefaultStrength] = useState<"" | MappingStrength>("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function choose(file: File) {
    setFileError(null); setResult(null);
    if (!/\.csv$/i.test(file.name)) { setFileError("Choose a .csv file."); return; }
    if (file.size > 2_000_000) { setFileError("The file is larger than 2 MB."); return; }
    try { setCsv(await file.text()); setFileName(file.name); }
    catch { setFileError("Could not read that file."); }
  }

  const ready = !!csv && sourceRef.trim().length >= 3 && licence.trim().length >= 2;

  return (
    <SheetCard label="Import a published crosswalk">
      <div style={{ ...text.hint, marginBottom: 10 }}>
        A CSV whose header names <span style={text.mono}>from_control</span> and <span style={text.mono}>to_control</span>, and
        optionally <span style={text.mono}>strength</span> and <span style={text.mono}>note</span>. Rows already decided are left alone.
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f); e.target.value = ""; }} />
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" style={small} onClick={() => fileRef.current?.click()}>{csv ? "Choose another file" : "Choose a CSV file"}</button>
          {fileName && <span style={text.hint}>{fileName}</span>}
        </div>
        {fileError && <div style={{ ...text.hint, color: theme.color.dangerText }}>{fileError}</div>}
        <label style={text.label}>Source name and version *
          <input value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} maxLength={300} placeholder="e.g. NIST OLIR ISO 27001:2022 to CSF 2.0, v1.1" style={{ ...inputStyle, marginTop: 4, padding: "6px 10px" }} />
        </label>
        <label style={text.label}>Licence or terms *
          <input value={licence} onChange={(e) => setLicence(e.target.value)} maxLength={300} placeholder="e.g. public domain (US government work)" style={{ ...inputStyle, marginTop: 4, padding: "6px 10px" }} />
        </label>
        <label style={text.label}>Strength where a row gives none
          <select value={defaultStrength} onChange={(e) => setDefaultStrength(e.target.value as "" | MappingStrength)} style={{ ...select, display: "block", marginTop: 4 }}>
            <option value="">The importer's default</option>
            {(Object.keys(STRENGTH_LABEL) as MappingStrength[]).map((s) => <option key={s} value={s}>{STRENGTH_LABEL[s]}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, ...text.body, cursor: "pointer" }}>
          <input type="checkbox" checked={approve} onChange={(e) => setApprove(e.target.checked)} />
          Approve on import (members see the rows at once)
        </label>
        <div>
          <button type="button" disabled={!ready || busy !== null} style={{ ...smallPrimary, opacity: !ready || busy ? 0.6 : 1 }}
            onClick={() => onRun("import",
              () => api.post<ImportResult>("/admin/control-mappings/import", {
                csv, sourceRef: sourceRef.trim(), licence: licence.trim(), approve, ...(defaultStrength ? { defaultStrength } : {}),
              }),
              (r) => { setResult(r); setCsv(null); setFileName(""); return `Imported: ${r.created} created, ${r.updated} updated, ${r.unchanged} already decided${r.errors.length ? `, ${r.errors.length} row error${r.errors.length === 1 ? "" : "s"}` : ""}.`; })}>
            {busy === "import" ? "Importing…" : "Import"}
          </button>
        </div>
        {result && result.errors.length > 0 && (
          <div style={{ maxHeight: 160, overflowY: "auto", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "6px 10px" }}>
            {result.errors.map((e, i) => <div key={i} style={{ ...text.meta, color: theme.color.dangerText }}>{e}</div>)}
          </div>
        )}
      </div>
    </SheetCard>
  );
}

// ── Ask the AI to draft mappings ─────────────────────────────────────────────

function DraftWithAi({ busy, frameworks, runs, fwLabel, onRun, onShowRun }: {
  busy: string | null;
  frameworks: AdminData["frameworks"];
  runs: DraftRun[];
  fwLabel: (id: string) => string;
  onRun: RunFn;
  onShowRun: (id: string) => void;
}) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const running = runs.some((r) => r.status === "queued" || r.status === "running");
  const ready = !!from && !!to && from !== to && !running;

  return (
    <SheetCard label="Ask the AI to draft mappings">
      <div style={{ ...text.hint, marginBottom: 10 }}>
        The platform model proposes mappings between two standards' controls. Every draft arrives as proposed and
        stays invisible to members until you approve it.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <select value={from} onChange={(e) => setFrom(e.target.value)} style={select} aria-label="From standard">
          <option value="">From standard</option>
          {frameworks.map((f) => <option key={f.id} value={f.id}>{f.label} ({f.controls})</option>)}
        </select>
        <select value={to} onChange={(e) => setTo(e.target.value)} style={select} aria-label="To standard">
          <option value="">To standard</option>
          {frameworks.filter((f) => f.id !== from).map((f) => <option key={f.id} value={f.id}>{f.label} ({f.controls})</option>)}
        </select>
        <button type="button" disabled={!ready || busy !== null} style={{ ...smallPrimary, opacity: !ready || busy ? 0.6 : 1 }}
          onClick={() => onRun("draft", () => api.post<{ run: DraftRun }>("/admin/control-mappings/drafts", { fromFrameworkId: from, toFrameworkId: to }),
            () => "Drafting pass queued. This page refreshes while it runs.")}>
          {busy === "draft" ? "Queuing…" : "Draft mappings"}
        </button>
      </div>
      {running && <div style={{ ...text.hint, marginBottom: 8 }}>A drafting pass is running; another can start once it finishes.</div>}
      {runs.length > 0 && (
        <div style={{ display: "grid", gap: 4 }}>
          <div style={text.label}>Recent runs</div>
          {runs.slice(0, 8).map((r) => {
            const failed = r.status === "error";
            const tone = failed ? { color: theme.color.dangerText, border: theme.color.danger }
              : r.status === "done" ? { color: theme.color.successText, border: theme.color.success }
              : { color: theme.color.warningText, border: theme.color.warning };
            return (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start", padding: "4px 0", borderBottom: `1px solid ${theme.color.border}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ ...text.body, fontSize: 12 }}>{fwLabel(r.fromFrameworkId)} to {fwLabel(r.toFrameworkId)}</div>
                  <div style={text.meta}>
                    {fmtDateTime(r.createdAt)}{r.proposed != null ? ` · ${r.proposed} proposed` : ""}
                  </div>
                  {r.error && <div style={{ ...text.meta, color: theme.color.dangerText }}>{r.error}</div>}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  <Tag color={tone.color} border={tone.border}>{RUN_STATUS_LABEL[r.status] ?? r.status}</Tag>
                  {r.status === "done" && (r.proposed ?? 0) > 0 && (
                    <button type="button" style={{ ...small, padding: "3px 8px" }} onClick={() => onShowRun(r.id)}>Review</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SheetCard>
  );
}
